import React, { useEffect, useState } from 'react';
import { IPlatformFilteringParams } from '../dto/IPlatformFilteringParams';
import { PlatformService } from '../PlatformService';
import { ErrorToast } from '../../../utils/toasters';
import { NavLink } from 'react-router-dom';
import { Button, Col, Form, Row, Table } from 'react-bootstrap';
import { Platform } from '../../../model/platform/Platform';
import ToggleChip from '../../../shared/components/ToggleChip';
import TableLayout from '../../../shared/layout/table/TableLayout';
import { ITableColumn } from '../../../shared/components/TableSkeleton';

const PLATFORM_COLUMNS: ITableColumn[] = [
    { label: 'ID' },
    { label: 'Icon' },
    { label: 'Name' },
    { label: 'Is enabled' },
    { label: 'Has api' },
    { label: 'Position' },
    { label: 'CPM' },
    { label: 'Action', className: 'text-end' },
];

export function PlatformList() {
    const [change, setChange] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [platforms, setPlatform] = useState<Platform[]>([]);
    const [state, setState] = useState<IPlatformFilteringParams>({
        name: '',
        page: 1,
        perPage: 15,
    });

    useEffect(() => {
        let isCurrent = true;
        setDataLoading(true);

        PlatformService.getPlatforms(state)
            .then(data => {
                if (!isCurrent) return;
                setPlatform(data.data);
                setTotalPages(Math.ceil(data.count / state.perPage));
            })
            .catch(err => {
                if (isCurrent) ErrorToast(err);
            })
            .finally(() => {
                if (isCurrent) setDataLoading(false);
            });

        return () => {
            isCurrent = false;
        };
    }, [change, page]);

    function disablePlatform(id: number) {
        PlatformService.disablePlatform(id)
            .then(data => {
                setPlatform(prev => prev.map(item => (item?.id === data.id ? data : item)));
            })
            .catch(err => ErrorToast(err));
    }

    const handlePages = (updatePage: number) => {
        setState({ ...state, page: updatePage });
        setPage(updatePage);
    };

    function handleChange(evt: any) {
        const value = evt.target.value?.trim();
        setState({
            ...state,
            [evt.target.name]: value,
        });
    }

    function handleClick(env: any) {
        env.preventDefault();
        setChange(!change);
    }

    function handleCheckbox(id: number) {
        PlatformService.hasApiToggle(id)
            .then(data => {
                setPlatform(prev => prev.map(item => (item?.id === data.id ? data : item)));
            })
            .catch(err => ErrorToast(err));
    }

    return (
        <>
            <NavLink
                className="btn btn-outline-primary"
                to={{
                    pathname: '/platform/form/',
                }}
            >
                + Add platform
            </NavLink>

            <div
                className="card mb-5 p-4 mt-3"
                onKeyDown={e => {
                    if (e.key === 'Enter') handleClick(e);
                }}
            >
                <Row>
                    <Form.Group as={Col} className="col-4 mb-0">
                        <Form.Control
                            placeholder="Name"
                            className="autocomplete-input"
                            name="name"
                            onChange={handleChange}
                        />
                    </Form.Group>
                    <Form.Group as={Col} className="mb-0">
                        <Button type="submit" onClick={handleClick}>
                            Search
                        </Button>
                    </Form.Group>
                </Row>
            </div>

            <TableLayout
                pagination={{ page, totalPages }}
                listLength={platforms.length}
                handlePages={handlePages}
                isLoading={dataLoading}
                dataName="platforms"
                skeletonColumns={PLATFORM_COLUMNS}
                skeletonRows={state.perPage}
            >
                <Table hover responsive>
                    <thead>
                        <tr>
                            {PLATFORM_COLUMNS.map(column => (
                                <th key={column.label} className={column.className}>
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {platforms.map(platform => (
                            <tr key={platform.id}>
                                <td>{platform.id}</td>
                                <td>
                                    <img
                                        alt=""
                                        src={platform.path || '/images/placeholder.png'}
                                        style={{ width: 35, height: 35 }}
                                    />
                                </td>
                                <td>{platform.name}</td>
                                <td>
                                    <ToggleChip
                                        name="isEnabled"
                                        onChange={() => disablePlatform(platform.id ?? 0)}
                                        checked={!!platform.isEnabled}
                                        label={platform.isEnabled ? 'Enabled' : 'Disabled'}
                                    />
                                </td>
                                <td>
                                    <ToggleChip
                                        name="hasApi"
                                        onChange={() => handleCheckbox(platform.id)}
                                        checked={!!platform.hasApi}
                                        label={platform.hasApi ? 'Has Api' : 'No Api'}
                                    />
                                </td>
                                <td>{platform.position}</td>
                                <td>{platform.predefinedCPM}</td>
                                <td className="text-end">
                                    <NavLink className="btn btn-primary" to={'/platform/form/' + platform.id}>
                                        Edit
                                    </NavLink>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </TableLayout>
        </>
    );
}
